import { useState, useContext, useRef, useEffect } from "react";
import { Box, Grid, IconButton } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import "../tasks/AddTasks.scss";
import CustomizedDatePicker from "../../components/CustomizedDatePicker";
import { useTranslation } from "react-i18next";
import CustomTextField from "../../components/DescriptionBox";
import AppContext from "../../config/AppContext";
import CustomButton from "../../components/CustomButton";
import { useLocation, useNavigate } from "react-router-dom";
import { getAllHRManagerProjects, listProject, } from "../../services/ProjectService";
import { toast } from "sonner";
import CommonUtil from "../../util/CommonUtils";
import { createTask } from "../../services/TaskService";
import CustomProjectSelect from "../../components/CustomProjectAutoComplete";
import moment from "moment/moment";
import "./TaskPopup.scss";
import { ReactComponent as CloseIcon } from "../../assets/images/ralvie/CloseReport.svg";
import { ReactComponent as CloseIconDark } from "../../assets/images/ralvie/CloseReportDark.svg";


export default function TaskPopup({ open, onClose, onTaskCreated, selectedProject }) {
    const { t, i18n } = useTranslation();
    const [title, setTitle] = useState("");
    const [projectId, setProjectId] = useState("");
    const [timelineStartDate, setTimelineStartDate] = useState(null);
    const [timelineEndDate, setTimelineEndDate] = useState(null);
    const [isSaveButtonDisable, setSaveButtonDisable] = useState(false);
    const [projects, setProjects] = useState([]);
    const { mode } = useContext(AppContext);
    const roleCode = CommonUtil.getRoleCode();

    const [state, setState] = useState({
        title: "",
        description: "",
        projectId: "",
        startDate: "",
        endDate: "",
        dueDate: "",
    });

    const [errors, setErrors] = useState({
        title: "",
        description: "",
        projectId: "",
        startDate: "",
        endDate: "",
    });
    useEffect(() => {
        if (selectedProject) {
            setProjectId(selectedProject.id);
        }
    }, [selectedProject]);
    const projectStartDate = selectedProject
        ? new Date(selectedProject.startDate)
        : null;
    const projectEndDate = selectedProject
        ? new Date(selectedProject.endDate)
        : null;

    const handleProjectSet = (value) => {
        const project = (projects || []).find((p) => p.id === value);
        setProjectId(value);
        const previousProjectId = projectId;

        if (!value || value !== previousProjectId) {
            setTimelineStartDate(null);
            setTimelineEndDate(null);
            setErrors((prevErrors) => ({
                ...prevErrors,
                timelineStartDate: "",
                timelineEndDate: "",
            }));
        }
        if (value && value === previousProjectId && project) {
            const projectStart = project ? new Date(project.startDate) : null;
            const projectEnd = project ? new Date(project.endDate) : null;

            let validStartDate = timelineStartDate;
            let validEndDate = timelineEndDate;

            if (
                timelineStartDate &&
                (timelineStartDate < projectStart || timelineStartDate > projectEnd)
            ) {
                validStartDate = null;
            }

            if (
                timelineEndDate &&
                (timelineEndDate < projectStart || timelineEndDate > projectEnd)
            ) {
                validEndDate = null;
            }

            setTimelineStartDate(validStartDate);
            setTimelineEndDate(validEndDate);
        }

        setErrors({
            ...errors,
            projectId: "",
            timelineStartDate: "",
            timelineEndDate: "",
        });
    };

    const handleCancelButton = () => {
        onClose();
    };

    const handleDescriptionChange = (e) => {
        let inputValue = e.target.value;
        let chars = Array.from(inputValue);

        if (chars.length > 2000) {
            inputValue = chars.slice(0, 2000).join('');
        }

        setState((prevState) => ({
            ...prevState,
            description: inputValue,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            description: "",
        }));
    };

    useEffect(() => {
        if (textAreaRef.current) {
            textAreaRef.current.style.height = "auto";
            const { scrollHeight } = textAreaRef.current;
            const height = Math.max(40, Math.min(scrollHeight, 300));
            textAreaRef.current.style.height = `${height}px`;
        }
    }, [state.description]);

    const textAreaRef = useRef(null);
    const validateFields = () => {
        let valid = true;
        const newErrors = { ...errors };

        Object.keys(newErrors).forEach((key) => {
            newErrors[key] = "";
        });

        if (!title.trim()) {
            newErrors.title = t("RAL0001");
            valid = false;
        } else if (title.length < 1) {
            newErrors.title = t("RAL1133");
            valid = false;
        } else if (title.length > 255) {
            newErrors.title = t("RAL1134");
            valid = false;
        }

        if (state.description && state.description.length > 2000) {
            newErrors.description = t("RAL0550");
            valid = false;
        }

        if (!timelineStartDate && selectedProject != null) {
            newErrors.timelineStartDate = t("RAL0551");
            valid = false;
        }

        if (
            !timelineEndDate &&
            projectEndDate != null &&
            moment(projectEndDate).isValid()
        ) {
            newErrors.timelineEndDate = t("RAL0552");
            valid = false;
        }

        if (
            timelineStartDate &&
            timelineEndDate &&
            timelineStartDate > timelineEndDate &&
            projectEndDate != null
        ) {
            newErrors.timelineStartDate = t("RAL0553");
            newErrors.timelineEndDate = t("RAL0554");
            valid = false;
        }

        if (!projectId.trim()) {
            newErrors.projectId = t("RAL0001");
            valid = false;
        }

        setErrors(newErrors);

        return valid;
    };

    const handleCreateTask = () => {
        if (isSaveButtonDisable) return;
        if (validateFields()) {
            const payload = {
                title: title.trim(),
                description: state.description.trim(),
                startDate: timelineStartDate,
                endDate: timelineEndDate,
                projectId: projectId,
                dueDate: timelineEndDate,
            };
            setSaveButtonDisable(true);
            createTask(payload)
                .then((res) => {
                    if (res.data.code === "RCI0051") {
                        toast.success(t("RAL1107"));
                        if (onTaskCreated) {
                            onTaskCreated({
                                id: res.data.data.id,
                                taskId: res.data.data.taskId,
                                title: res.data.data.title,
                                projectId: res.data.data.projectId
                            });
                        }
                        onClose();
                    } else {
                        toast.error(res.data.message);
                    }
                })
                .catch((error) => {
                    console.error("Error creating task:", error);
                    toast.error(t("RAL0326"));
                })
                .finally(() => {
                    setSaveButtonDisable(false);
                });
        }
    };
    useEffect(() => {
        setErrors({});
    }, [i18n.language]);

    const handleTimelineStartDate = (date) => {
        if (date === null || date === "" || (date && isNaN(date.valueOf()))) {
            setTimelineStartDate(null);
            setErrors((prevErrors) => ({
                ...prevErrors,
                timelineStartDate: t("RAL0551"),
            }));
        } else {
            setTimelineStartDate(date);
            if (timelineEndDate && date && date > timelineEndDate) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    timelineStartDate: t("RAL0553"),
                    timelineEndDate: t("RAL0554"),
                }));
            } else {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    timelineStartDate: "",
                    timelineEndDate: "",
                }));
            }
        }
    };

    const handleTimelineEndDate = (date) => {
        if (date === null || date === "" || (date && isNaN(date.valueOf()))) {
            setTimelineEndDate(null);
            if (
                projectStartDate &&
                projectEndDate &&
                moment(projectEndDate).isValid()
            ) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    timelineEndDate: t("RAL0552"),
                }));
            }
            if (date === null) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    timelineStartDate: "",
                    timelineEndDate: "",
                }));
            }
        } else {
            setTimelineEndDate(date);
            if (timelineStartDate && date && timelineStartDate > date) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    timelineStartDate: t("RAL0553"),
                    timelineEndDate: t("RAL0554"),
                }));
            } else {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    timelineStartDate: "",
                    timelineEndDate: "",
                }));
            }
        }
    };

    useEffect(() => {
        const loadProjectsBasedOnRole = async () => {
            try {
                if (roleCode === "HR_MANAGER") {
                    const res = await getAllHRManagerProjects();
                    if (res?.data?.code === "RCI0000" && Array.isArray(res?.data?.data)) {
                        setProjects(res.data.data);
                    } else {
                        setProjects([]);
                    }
                } else {
                    const res = await listProject();
                    if (
                        res?.data?.code === "RCI0000" &&
                        Array.isArray(res.data.data.projects)
                    ) {
                        const filteredProjects = res.data.data.projects.filter(
                            (project) =>
                                project &&
                                project.status !== "ARCHIVED" &&
                                project.status !== "COMPLETED"
                        );
                        setProjects(filteredProjects);
                    } else {
                        setProjects([]);
                    }
                }
            } catch (error) {
                console.error("Error fetching projects:", error);
                setProjects([]);
            }
        };

        loadProjectsBasedOnRole();
    }, []);
    if (!open) return null;

    return (
        <div className="backdrop"  >
            <div className={mode === "dark" ? "addnewtask-popup-dark" : "addnewtask-popup"}>
                <div>
                    <div className="title-dimension-popup">
                        <div>
                            <span className={mode === "dark" ? "addnewtask-heading-dark" : "addnewtask-heading"}>{t("RAL1197")}</span>
                        </div>
                        <div>
                            <span
                                onClick={handleCancelButton}
                                style={{ cursor: "pointer" }}
                            >
                                {mode === "light" ? <CloseIcon /> : <CloseIconDark />}
                            </span>
                        </div>
                    </div>
                    <div>
                        <div className="tasktextfield1">
                            <CustomTextField
                                required={true}
                                id="text-create-taskname"
                                name="TaskTile"
                                label={t("RAL1109")}
                                placeholder={t("RAL1110")}
                                error={errors.title}
                                helperText={errors.title}
                                value={title}
                                onChange={(e) => {
                                    let inputValue = e.target.value;
                                    let chars = Array.from(inputValue);
                                    if (chars.length > 255) {
                                        inputValue = chars.slice(0, 255).join('');
                                    }
                                    setTitle(inputValue);
                                    if (inputValue.trim() !== "") {
                                        setErrors((prevErrors) => ({
                                            ...prevErrors,
                                            title: "",
                                        }));
                                    }
                                }}
                                inputProps={{
                                    maxLength: 255,
                                }}
                            />
                            <div className="task-description-counter">
                                {" "}
                                {title?.length}/255
                            </div>
                        </div>

                        <div className="task-description-wrapper1">
                            <CustomTextField
                                id="text-task-description"
                                name="Description"
                                label={t("RAL0292")}
                                rows={1}
                                placeholder={t("RAL0293")}
                                error={errors.description}
                                helperText={errors.description}
                                value={state.description}
                                inputRef={textAreaRef}
                                multiline
                                onChange={handleDescriptionChange}
                                inputProps={{
                                    maxLength: 2000,
                                }}
                                className="task-description-field"
                            />
                            <div className="task-description-counter">
                                {" "}
                                {state.description?.length}/2000
                            </div>
                        </div>

                        <div className="addtaskProject">
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 1,
                                }}
                            >
                                <label id="task-text-project">
                                    {t("RAL0288")}
                                    <span className="starRedColor">*</span>
                                </label>
                            </Box>
                            <CustomProjectSelect
                                id="dropdown-task-create-project"
                                options={projects || []}
                                error={Boolean(errors.projectId)}
                                placeholder={t("RAL1163")}
                                value={projectId}
                                helperText={errors.projectId || ""}
                                onChange={(newValue) => handleProjectSet(newValue)}
                                disabled={!!selectedProject}
                                handleClear={() => {
                                    if (!selectedProject) {
                                        setProjectId("");
                                        setTimelineStartDate(null);
                                        setTimelineEndDate(null);
                                        setErrors((prevErrors) => ({
                                            ...prevErrors,
                                            projectId: "",
                                            timelineStartDate: "",
                                            timelineEndDate: "",
                                        }));
                                    }
                                }}
                            />
                            <div className="task-project-timeline">
                                {t("RAL1135")}
                                {selectedProject?.startDate &&
                                    moment(selectedProject.startDate).format("DD-MM-YYYY")}
                                {selectedProject?.startDate &&
                                    selectedProject?.endDate &&
                                    moment(selectedProject.endDate).isValid() &&
                                    ` ${t("RAL1002")} ${moment(selectedProject.endDate).format("DD-MM-YYYY")}`}
                            </div>
                        </div>

                        <div className="datepicker-dimension-popup">
                            <div className="datepicker-dimension111">
                                <CustomizedDatePicker
                                    required={!!selectedProject}
                                    id="btn-date-task"
                                    name="btn-date-task"
                                    label={t("RAL1113")}
                                    placeholder={t("RAL1039")}
                                    disabled={!selectedProject}
                                    value={timelineStartDate}
                                    error={Boolean(errors.timelineStartDate)}
                                    helperText={errors.timelineStartDate || ""}
                                    onChange={handleTimelineStartDate}
                                    minDate={selectedProject?.startDate ? new Date(selectedProject.startDate) : undefined}
                                    maxDate={selectedProject?.endDate ? new Date(selectedProject.endDate) : undefined}
                                />

                            </div>
                            <div className="datepicker-dimension111">
                                <CustomizedDatePicker
                                    required={
                                        selectedProject?.startDate &&
                                        selectedProject?.endDate &&
                                        moment(selectedProject.endDate).isValid()
                                    }
                                    id="btn-date-task"
                                    name="btn-date-task"
                                    label={t("RAL1114")}
                                    placeholder={t("RAL1040")}
                                    isEndDateClear={
                                        !(
                                            selectedProject?.startDate &&
                                            selectedProject?.endDate &&
                                            moment(selectedProject.endDate).isValid()
                                        )
                                    }
                                    disabled={!selectedProject}
                                    value={timelineEndDate}
                                    onChange={handleTimelineEndDate}
                                    error={Boolean(errors.timelineEndDate)}
                                    helperText={errors.timelineEndDate || ""}
                                    minDate={
                                        timelineStartDate ||
                                        (selectedProject?.startDate ? new Date(selectedProject.startDate) : new Date())
                                    }
                                    maxDate={selectedProject?.endDate ? new Date(selectedProject.endDate) : undefined}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="popup-buttons-task">
                        <div>

                        </div>
                        <div className="popup-buttons-task">
                            <CustomButton
                                place="sp-cancel"
                                id="btn-cancel-project-report"
                                name="btn-cancel-member"
                                variant="outlined"
                                size="large"
                                fullWidth="fullWidth"
                                height="50px"
                                minWidth="150px"
                                onClick={handleCancelButton}
                            >
                                {t("RAL0177")}
                            </CustomButton>
                            <CustomButton
                                id="btn-add-project-report"
                                name="btn-add-project-report"
                                variant="contained"
                                size="large"
                                fullWidth="fullWidth"
                                height="50px"
                                minWidth="150px"
                                onClick={handleCreateTask}

                            >
                                {t("RAL1124")}
                            </CustomButton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
